import React, { useState, useEffect, useCallback } from 'react';
import { K } from '../constants';
import { useWeather } from '../hooks/useWeather';
import { useLocation } from '../hooks/useLocation';

const BIG = { fontFamily: K.fSans, fontWeight: 700, letterSpacing: 0.2, lineHeight: 1.1 };
const LBL = { fontFamily: K.fSans, fontSize: 11, color: K.t3, letterSpacing: 0.4, fontWeight: 600 };

const MODE_CARDS = [
  { id: 'ops',      label: 'Pump Ops',  sub: 'Hose layouts, PDPs, water supply, relay pumping, and apparatus specs.', col: K.red },
  { id: 'ems',      label: 'EMS',       sub: 'CPR timer, drug calculator, patient assessments, and ECG practice.',    col: K.ems },
  { id: 'command',  label: 'Command',   sub: 'Incident management, unit assignment, timekeeping, and checklists.',    col: K.info },
  { id: 'training', label: 'Training',  sub: 'Quizzes, flashcards, leaderboard, and study resources.',                col: K.gold },
];

/* All tools available for the quick tools grid */
const ALL_TOOLS = [
  { id: 'cprtimer',    mode: 'ems',      label: 'CPR Timer',     col: K.ems },
  { id: 'drugcalc',    mode: 'ems',      label: 'Drug Calc',     col: K.ems },
  { id: 'burncalc',    mode: 'ems',      label: 'Burn Calc',     col: K.ems },
  { id: 'assessment',  mode: 'ems',      label: 'Assessments',   col: K.ems },
  { id: 'pediatric',   mode: 'ems',      label: 'Pediatric',     col: K.ems },
  { id: 'ecg',         mode: 'ems',      label: 'ECG Practice',  col: K.ems },
  { id: 'layout',      mode: 'ops',      label: 'Hose Layout',   col: K.red },
  { id: 'custom',      mode: 'ops',      label: 'Quick Calc',    col: K.red },
  { id: 'supply',      mode: 'ops',      label: 'Water Supply',  col: K.red },
  { id: 'relay',       mode: 'ops',      label: 'Relay Pump',    col: K.red },
  { id: 'pc',          mode: 'ops',      label: 'Preconnects',   col: K.red },
  { id: 'apparatus',   mode: 'ops',      label: 'Apparatus',     col: K.red },
  { id: 'resources',   mode: 'ops',      label: 'Resources',     col: K.red },
  { id: 'flref',       mode: 'ops',      label: 'FL Reference',  col: K.red },
  { id: 'gpm',         mode: 'ops',      label: 'GPM Calc',      col: K.red },
  { id: 'draft',       mode: 'ops',      label: 'Drafting',      col: K.red },
  { id: 'incident',    mode: 'command',  label: 'Incident Cmd',  col: K.info },
  { id: 'history',     mode: 'command',  label: 'Inc. History',  col: K.info },
  { id: 'evolutions',  mode: 'training', label: 'Evolutions',    col: K.gold },
  { id: 'quiz',        mode: 'training', label: 'Quiz',          col: K.gold },
  { id: 'flashcards',  mode: 'training', label: 'Flashcards',    col: K.gold },
  { id: 'leaderboard', mode: 'training', label: 'Leaderboard',   col: K.gold },
  { id: 'chat',        mode: null,       label: 'Team Chat',     col: K.teal },
  { id: 'radio',       mode: null,       label: 'Radio',         col: K.teal },
  { id: 'notices',     mode: null,       label: 'Notices',       col: K.warn },
];

const DEFAULT_QUICK = ['cprtimer', 'drugcalc', 'layout', 'custom', 'quiz', 'chat'];
const STORAGE_KEY = 'hc_quick_tools';
const RECENT_KEY = 'hc_recent_tools';
const QUICK_MODE_KEY = 'hc_quick_mode'; // 'default' | 'recent'
const MAX_RECENT = 6;

function loadQuickTools() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const ids = JSON.parse(saved);
      if (Array.isArray(ids) && ids.length > 0) return ids;
    }
  } catch { /* ignore */ }
  return DEFAULT_QUICK;
}

function loadRecentTools() {
  try {
    const saved = localStorage.getItem(RECENT_KEY);
    if (saved) {
      const ids = JSON.parse(saved);
      if (Array.isArray(ids)) return ids;
    }
  } catch { /* ignore */ }
  return [];
}

function loadQuickMode() {
  try {
    const saved = localStorage.getItem(QUICK_MODE_KEY);
    if (saved === 'recent') return 'recent';
  } catch { /* ignore */ }
  return 'default';
}


/* Location picker inside weather detail */
function LocationPicker({ location, geoStatus, locateMe, setCustomLocation, resetToDefault }) {
  const [editing, setEditing] = useState(false);
  const [latVal, setLatVal] = useState('');
  const [lngVal, setLngVal] = useState('');
  const [nameVal, setNameVal] = useState('');

  const startEdit = (e) => {
    e.stopPropagation();
    setLatVal(String(location.lat));
    setLngVal(String(location.lng));
    setNameVal(location.name || '');
    setEditing(true);
  };

  const save = (e) => {
    e.stopPropagation();
    const lat = parseFloat(latVal);
    const lng = parseFloat(lngVal);
    if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      setCustomLocation(lat, lng, nameVal.trim() || undefined);
      setEditing(false);
    }
  };

  const handleGps = (e) => {
    e.stopPropagation();
    locateMe();
  };

  const handleReset = (e) => {
    e.stopPropagation();
    resetToDefault();
    setEditing(false);
  };

  const inpSt = {
    background: K.bg2, border: `1px solid ${K.bdr}`, borderRadius: 6, color: K.t0,
    fontFamily: "'DM Mono', monospace", fontSize: 12, padding: '6px 8px',
    width: '100%', boxSizing: 'border-box', outline: 'none', fontWeight: 500,
  };

  return (
    <div style={{ background: K.bg, borderRadius: 8, padding: '8px 12px', marginBottom: 8 }} onClick={(e) => e.stopPropagation()}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 9, color: K.t3, letterSpacing: 1, textTransform: 'uppercase', fontWeight: 600 }}>Location</div>
          <div style={{ fontSize: 12, color: K.t1, fontWeight: 600, marginTop: 2 }}>
            {location.name || `${location.lat}, ${location.lng}`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {!editing && (
            <>
              <button onClick={handleGps} disabled={geoStatus === 'requesting'} style={{
                background: K.bg2, border: `1px solid ${K.bdr}`, borderRadius: 5, padding: '3px 8px',
                cursor: geoStatus === 'requesting' ? 'default' : 'pointer', fontSize: 9, fontWeight: 700,
                color: K.teal, letterSpacing: 0.5, textTransform: 'uppercase', opacity: geoStatus === 'requesting' ? 0.5 : 1,
              }}>{geoStatus === 'requesting' ? '...' : 'GPS'}</button>
              <button onClick={startEdit} style={{
                background: K.bg2, border: `1px solid ${K.bdr}`, borderRadius: 5, padding: '3px 8px',
                cursor: 'pointer', fontSize: 9, fontWeight: 700, color: K.t3, letterSpacing: 0.5, textTransform: 'uppercase',
              }}>Edit</button>
            </>
          )}
        </div>
      </div>
      {editing && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <input value={nameVal} onChange={(e) => setNameVal(e.target.value)} placeholder="Location name" style={inpSt} onClick={(e) => e.stopPropagation()} />
          <div style={{ display: 'flex', gap: 6 }}>
            <input value={latVal} onChange={(e) => setLatVal(e.target.value)} placeholder="Latitude" type="number" step="any" style={inpSt} onClick={(e) => e.stopPropagation()} />
            <input value={lngVal} onChange={(e) => setLngVal(e.target.value)} placeholder="Longitude" type="number" step="any" style={inpSt} onClick={(e) => e.stopPropagation()} />
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={save} style={{
              flex: 1, background: K.teal, color: '#fff', border: 'none', borderRadius: 6, padding: '7px 0',
              cursor: 'pointer', fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
            }}>Save</button>
            <button onClick={handleReset} style={{
              background: K.bg2, border: `1px solid ${K.bdr}`, borderRadius: 6, padding: '7px 10px',
              cursor: 'pointer', fontSize: 10, fontWeight: 600, color: K.t3, letterSpacing: 0.5, textTransform: 'uppercase',
            }}>Default</button>
            <button onClick={(e) => { e.stopPropagation(); setEditing(false); }} style={{
              background: K.bg2, border: `1px solid ${K.bdr}`, borderRadius: 6, padding: '7px 10px',
              cursor: 'pointer', fontSize: 10, fontWeight: 600, color: K.t3,
            }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function fmtVis(meters) {
  if (meters == null) return '—';
  const miles = meters / 1609.34;
  if (miles >= 10) return `${Math.round(miles)} mi`;
  return `${Math.round(miles * 10) / 10} mi`;
}

function fmtSunTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch { return '—'; }
}

function uvLabel(uv) {
  if (uv == null) return '';
  if (uv >= 11) return 'Extreme';
  if (uv >= 8) return 'Very High';
  if (uv >= 6) return 'High';
  if (uv >= 3) return 'Moderate';
  return 'Low';
}

function uvColor(uv) {
  if (uv == null) return K.t1;
  if (uv >= 11) return '#7c3aed';
  if (uv >= 8) return K.bad;
  if (uv >= 6) return '#f97316';
  if (uv >= 3) return K.warn;
  return K.ok;
}

/* Expanded weather detail panel */
function WeatherDetail({ weather, alerts, location, geoStatus, locateMe, setCustomLocation, resetToDefault }) {
  const tempCol = weather.temp >= 90 ? K.bad : weather.temp <= 32 ? K.info : K.t0;
  const row = { display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: `1px solid ${K.bdr}`, fontSize: 13 };

  return (
    <div style={{ marginTop: 8, borderTop: `1px solid ${K.bdr}`, paddingTop: 8 }}>
      {/* Temp + Wind hero */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 6 }}>
        <div style={{ background: K.bg, borderRadius: 6, padding: '8px 10px', textAlign: 'center' }}>
          <div style={{ fontFamily: "'Exo 2', sans-serif", fontSize: 28, fontWeight: 900, color: tempCol, lineHeight: 1 }}>{weather.temp}°F</div>
          <div style={{ fontSize: 11, color: K.t3, marginTop: 2 }}>
            {weather.feelsLike !== weather.temp ? `Feels ${weather.feelsLike}° · ` : ''}{weather.high != null ? `H ${weather.high}° / L ${weather.low}°` : weather.condition}
          </div>
        </div>
        <div style={{ background: K.bg, borderRadius: 6, padding: '8px 10px', textAlign: 'center' }}>
          <div style={{ fontFamily: "'Exo 2', sans-serif", fontSize: 28, fontWeight: 900, color: K.t0, lineHeight: 1 }}>{weather.windSpeed}<span style={{ fontSize: 14, fontWeight: 700, color: K.t3 }}> mph</span></div>
          <div style={{ fontSize: 11, color: K.t3, marginTop: 2 }}>{weather.windDir} · Gusts {weather.gusts}{weather.maxWind != null ? ` · Max ${weather.maxWind}` : ''}</div>
        </div>
      </div>

      {/* Quick stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginBottom: 6 }}>
        <div style={{ background: K.bg, borderRadius: 6, padding: '6px 8px', textAlign: 'center' }}>
          <div style={{ fontFamily: "'Exo 2', sans-serif", fontSize: 18, fontWeight: 900, color: weather.humidity >= 80 ? K.info : K.t0, lineHeight: 1 }}>{weather.humidity}%</div>
          <div style={{ fontSize: 10, color: K.t3, marginTop: 2 }}>Humidity</div>
        </div>
        <div style={{ background: K.bg, borderRadius: 6, padding: '6px 8px', textAlign: 'center' }}>
          <div style={{ fontFamily: "'Exo 2', sans-serif", fontSize: 18, fontWeight: 900, color: uvColor(weather.uvIndex), lineHeight: 1 }}>{weather.uvIndex ?? '—'}</div>
          <div style={{ fontSize: 10, color: uvColor(weather.uvIndex), marginTop: 2 }}>UV {uvLabel(weather.uvIndex)}</div>
        </div>
        <div style={{ background: K.bg, borderRadius: 6, padding: '6px 8px', textAlign: 'center' }}>
          <div style={{ fontFamily: "'Exo 2', sans-serif", fontSize: 18, fontWeight: 900, color: K.t0, lineHeight: 1 }}>{weather.cloudCover ?? '—'}%</div>
          <div style={{ fontSize: 10, color: K.t3, marginTop: 2 }}>Clouds</div>
        </div>
      </div>

      {/* Conditions table */}
      <div style={{ background: K.bg, borderRadius: 6, padding: '6px 10px', marginBottom: 6 }}>
        <div style={row}>
          <span style={{ color: K.t2 }}>Condition</span>
          <span style={{ color: K.t1, fontWeight: 600 }}>{weather.condition}</span>
        </div>
        <div style={row}>
          <span style={{ color: K.t2 }}>Dew Point</span>
          <span style={{ color: K.t1, fontWeight: 600 }}>{weather.dewPoint != null ? `${weather.dewPoint}°F` : '—'}</span>
        </div>
        <div style={row}>
          <span style={{ color: K.t2 }}>Visibility</span>
          <span style={{ color: K.t1, fontWeight: 600 }}>{fmtVis(weather.visibility)}</span>
        </div>
        <div style={row}>
          <span style={{ color: K.t2 }}>Pressure</span>
          <span style={{ color: K.t1, fontWeight: 600 }}>{weather.pressure != null ? `${weather.pressure} hPa` : '—'}</span>
        </div>
        <div style={row}>
          <span style={{ color: K.t2 }}>Precip</span>
          <span style={{ color: weather.precipitation > 0 ? K.info : K.t1, fontWeight: 600 }}>{weather.precipitation != null ? `${weather.precipitation} in` : '—'}{weather.dailyPrecip > 0 ? ` (${weather.dailyPrecip} today)` : ''}</span>
        </div>
        {weather.temp >= 80 && (
          <div style={row}>
            <span style={{ color: K.t2 }}>Heat Index</span>
            <span style={{ color: weather.heatIndex >= 105 ? K.bad : weather.heatIndex >= 90 ? K.warn : K.t1, fontWeight: 600 }}>{weather.heatIndex}°F</span>
          </div>
        )}
        {weather.temp <= 50 && weather.windSpeed >= 3 && (
          <div style={row}>
            <span style={{ color: K.t2 }}>Wind Chill</span>
            <span style={{ color: weather.windChill <= 0 ? K.bad : weather.windChill <= 20 ? K.warn : K.t1, fontWeight: 600 }}>{weather.windChill}°F</span>
          </div>
        )}
        <div style={row}>
          <span style={{ color: K.t2 }}>WBGT (est.)</span>
          <span style={{ color: weather.rehab?.col || K.t1, fontWeight: 600 }}>{weather.wbgtF}°F / {weather.wbgt}°C</span>
        </div>
        {(weather.sunrise || weather.sunset) && (
          <div style={{ ...row, borderBottom: 'none' }}>
            <span style={{ color: K.t2 }}>Sun</span>
            <span style={{ color: K.t1, fontWeight: 600 }}>{fmtSunTime(weather.sunrise)} — {fmtSunTime(weather.sunset)}</span>
          </div>
        )}
      </div>

      <LocationPicker location={location} geoStatus={geoStatus} locateMe={locateMe} setCustomLocation={setCustomLocation} resetToDefault={resetToDefault} />

      {/* Rehab guidance */}
      {weather.rehab && (
        <div style={{
          background: `${weather.rehab.col}12`, border: `1px solid ${weather.rehab.col}33`,
          borderRadius: 6, padding: '8px 10px', marginBottom: 6,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.5, textTransform: 'uppercase', color: weather.rehab.col }}>
              Rehab: {weather.rehab.level}
            </div>
            {weather.rehab.work !== '—' && (
              <div style={{ fontSize: 11, color: K.t2 }}>
                Work {weather.rehab.work} / Rest {weather.rehab.rest}
              </div>
            )}
          </div>
          <div style={{ fontSize: 12, color: K.t2, lineHeight: 1.4 }}>{weather.rehab.note}</div>
        </div>
      )}

      {/* NOAA link */}
      <a
        href={`https://forecast.weather.gov/MapClick.php?lat=${location.lat}&lon=${location.lng}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        style={{
          display: 'block', background: K.bg, borderRadius: 6, padding: '8px 10px',
          marginBottom: 6, textDecoration: 'none', textAlign: 'center',
          border: `1px solid ${K.bdr}`,
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 700, color: K.teal, letterSpacing: 1, textTransform: 'uppercase', fontFamily: "'Exo 2', sans-serif" }}>
          View NOAA Forecast →
        </span>
      </a>

      {/* NWS Alerts */}
      {alerts.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {alerts.map((a) => {
            const severe = a.severity === 'Extreme' || a.severity === 'Severe';
            return (
              <div key={a.id} style={{
                background: `${severe ? K.bad : K.warn}15`,
                border: `1px solid ${severe ? K.bad : K.warn}44`,
                borderRadius: 6, padding: '6px 10px',
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: severe ? K.bad : K.warn, marginBottom: 2 }}>{a.event}</div>
                {a.headline && <div style={{ fontSize: 11, color: K.t2, lineHeight: 1.4 }}>{a.headline}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function HomePage({ onSelectMode, onQuickTool, incident, user, isEnabled = () => true }) {
  const [quickIds, setQuickIds] = useState(loadQuickTools);
  const [recentIds, setRecentIds] = useState(loadRecentTools);
  const [quickMode, setQuickMode] = useState(loadQuickMode);
  const [editing, setEditing] = useState(false);
  const [weatherOpen, setWeatherOpen] = useState(false);
  const { location, geoStatus, setCustomLocation, locateMe, resetToDefault } = useLocation();
  const { weather, alerts } = useWeather(location.lat, location.lng);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(quickIds));
  }, [quickIds]);

  useEffect(() => {
    localStorage.setItem(RECENT_KEY, JSON.stringify(recentIds));
  }, [recentIds]);

  useEffect(() => {
    localStorage.setItem(QUICK_MODE_KEY, quickMode);
  }, [quickMode]);

  const trackRecent = useCallback((toolId) => {
    setRecentIds((prev) => {
      const filtered = prev.filter((id) => id !== toolId);
      return [toolId, ...filtered].slice(0, MAX_RECENT);
    });
  }, []);

  const handleQuickTool = (toolId, toolMode) => {
    trackRecent(toolId);
    onQuickTool(toolId, toolMode);
  };

  const displayIds = quickMode === 'recent' && recentIds.length > 0 ? recentIds : quickIds;
  const quickTools = displayIds.map((id) => ALL_TOOLS.find((t) => t.id === id)).filter((t) => t && isEnabled(t.id));
  const availableToAdd = ALL_TOOLS.filter((t) => !quickIds.includes(t.id));

  const removeQuickTool = (id) => {
    setQuickIds((prev) => prev.filter((x) => x !== id));
  };

  const addQuickTool = (id) => {
    setQuickIds((prev) => [...prev, id]);
  };

  const resetDefaults = () => {
    setQuickIds(DEFAULT_QUICK);
  };

  const toggleMode = () => {
    setQuickMode((m) => m === 'default' ? 'recent' : 'default');
    setEditing(false);
  };

  return (
    <div>
      {/* Hero */}
      <div style={{ textAlign: 'center', padding: '18px 0 22px' }}>
        <div style={{ ...BIG, fontFamily: K.fSans, fontWeight: 800, fontSize: 34, color: K.t0 }}>Hydro<span style={{ color: K.red }}>Calc</span> <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, color: K.warn, background: `${K.warn}18`, border: `1px solid ${K.warn}44`, borderRadius: 4, padding: '2px 8px', verticalAlign: 'super' }}>Beta</span></div>
        <div style={{ fontFamily: K.fSans, fontSize: 12, color: K.t2, letterSpacing: 0.3, marginTop: 4, fontWeight: 500 }}>Central Mason Fire &amp; EMS</div>
        {user && (
          <div style={{ marginTop: 8, fontSize: 11, color: K.t3, fontWeight: 500 }}>
            Signed in as <span style={{ color: K.t1, fontWeight: 600 }}>{user.displayName || user.email}</span>
          </div>
        )}
      </div>

      {/* Weather Strip — clickable to expand */}
      {weather && (
        <div
          onClick={() => setWeatherOpen((o) => !o)}
          style={{ background: K.bg1, border: `1px solid ${K.bdr}`, borderRadius: 10, padding: '10px 14px', marginBottom: 12, cursor: 'pointer', transition: 'border-color 0.15s' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div>
                <div style={{ fontFamily: K.fMono, fontSize: 22, fontWeight: 500, color: weather.temp >= 90 ? K.bad : weather.temp <= 32 ? K.info : K.t0, lineHeight: 1 }}>
                  {weather.temp}°F
                </div>
                {weather.feelsLike !== weather.temp && (
                  <div style={{ fontSize: 9, color: K.t3, marginTop: 2 }}>Feels {weather.feelsLike}°</div>
                )}
              </div>
              <div style={{ width: 1, height: 28, background: K.bdr }} />
              <div>
                <div style={{ fontSize: 11, color: K.t1, fontWeight: 600 }}>{weather.condition}</div>
                <div style={{ fontSize: 10, color: K.t3, marginTop: 1, fontWeight: 500 }}>
                  Wind {weather.windDir} {weather.windSpeed} mph · {weather.humidity}% RH
                </div>
                <div style={{ fontSize: 8, color: K.t3, marginTop: 2, fontWeight: 500 }}>{location.name}</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {weather.rehab && weather.rehab.level !== 'MINIMAL' && (
                <div style={{
                  fontSize: 8, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase',
                  color: weather.rehab.col, background: `${weather.rehab.col}18`,
                  padding: '3px 8px', borderRadius: 4, whiteSpace: 'nowrap',
                }}>
                  {weather.rehab.level}
                </div>
              )}
              <div style={{ color: K.t3, fontSize: 10, transition: 'transform 0.2s', transform: weatherOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</div>
            </div>
          </div>
          {!weatherOpen && alerts.length > 0 && (
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {alerts.slice(0, 2).map((a) => (
                <div key={a.id} style={{
                  background: `${a.severity === 'Extreme' || a.severity === 'Severe' ? K.bad : K.warn}15`,
                  border: `1px solid ${a.severity === 'Extreme' || a.severity === 'Severe' ? K.bad : K.warn}44`,
                  borderRadius: 6, padding: '5px 10px',
                }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: a.severity === 'Extreme' || a.severity === 'Severe' ? K.bad : K.warn }}>
                    {a.event}
                  </div>
                </div>
              ))}
            </div>
          )}
          {weatherOpen && <WeatherDetail weather={weather} alerts={alerts} location={location} geoStatus={geoStatus} locateMe={locateMe} setCustomLocation={setCustomLocation} resetToDefault={resetToDefault} />}
        </div>
      )}

      {/* Active Incident Banner */}
      {incident?.incident?.status === 'active' && (
        <button className="hc-btn" onClick={() => onQuickTool('incident', 'command')}
          style={{ width: '100%', background: `linear-gradient(135deg, ${K.info}22, ${K.info}11)`, border: `1.5px solid ${K.info}`, borderRadius: 10, padding: '12px 16px', cursor: 'pointer', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left' }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: K.info, boxShadow: `0 0 8px ${K.info}`, animation: 'pulse 1.5s infinite', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ ...BIG, fontSize: 14, color: K.info, marginBottom: 2 }}>Active Incident</div>
            <div style={{ fontSize: 11, color: K.t2 }}>
              {incident.incident.type || 'Incident'} — {incident.elapsedFmt || '00:00'}
            </div>
          </div>
          <div style={{ fontSize: 10, color: K.info, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', fontFamily: "'DM Mono', monospace" }}>View →</div>
        </button>
      )}

      {/* Quick Tools */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={LBL}>Quick Tools</div>
            {recentIds.length > 0 && (
              <button onClick={toggleMode} style={{
                background: K.bg2, border: `1px solid ${K.bdr}`, borderRadius: 4,
                padding: '2px 6px', cursor: 'pointer', fontSize: 8, fontWeight: 700,
                color: K.t3, letterSpacing: 0.5, textTransform: 'uppercase',
                fontFamily: "'Exo 2', sans-serif",
              }}>{quickMode === 'default' ? 'Recent' : 'Default'}</button>
            )}
          </div>
          {quickMode === 'default' && (
            <button onClick={() => setEditing(!editing)} style={{
              background: editing ? `${K.teal}22` : 'transparent', border: `1px solid ${editing ? K.teal : K.bdr}`,
              borderRadius: 5, padding: '3px 8px', cursor: 'pointer', fontSize: 9, fontWeight: 700,
              color: editing ? K.teal : K.t3, letterSpacing: 1, textTransform: 'uppercase',
              fontFamily: "'Exo 2', sans-serif",
            }}>{editing ? 'Done' : 'Edit'}</button>
          )}
        </div>

        <div className="hc-grid-3" style={{ gap: 8 }}>
          {quickTools.map((t) => (
            <div key={t.id} style={{ position: 'relative' }}>
              <button className="hc-btn" onClick={() => !editing && handleQuickTool(t.id, t.mode)}
                style={{
                  width: '100%',
                  background: K.bg1,
                  border: `1.5px solid ${K.bdr}`,
                  borderLeft: `3px solid ${t.col}`,
                  borderRadius: 10,
                  padding: '14px 8px 12px',
                  cursor: editing ? 'default' : 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 4,
                  transition: 'all 0.15s',
                  opacity: editing ? 0.7 : 1,
                }}>
                <span style={{ fontFamily: K.fSans, fontSize: 13, fontWeight: 600, letterSpacing: 0.2, color: K.t1 }}>{t.label}</span>
              </button>
              {editing && quickMode === 'default' && (
                <button onClick={() => removeQuickTool(t.id)} style={{
                  position: 'absolute', top: -6, right: -6, width: 20, height: 20,
                  borderRadius: '50%', background: K.bad, border: '2px solid ' + K.bg,
                  color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  lineHeight: 1, padding: 0,
                }}>×</button>
              )}
            </div>
          ))}
        </div>

        {quickMode === 'recent' && recentIds.length === 0 && (
          <div style={{ textAlign: 'center', padding: '16px 0', color: K.t3, fontSize: 11 }}>
            No recent tools yet. Use some tools and they'll appear here.
          </div>
        )}

        {/* Edit mode: add tools + reset */}
        {editing && quickMode === 'default' && (
          <div style={{ marginTop: 12 }}>
            {availableToAdd.length > 0 && (
              <>
                <div style={{ ...LBL, fontSize: 9, marginBottom: 6, color: K.t3 }}>Add Tools</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {availableToAdd.map((t) => (
                    <button key={t.id} onClick={() => addQuickTool(t.id)} style={{
                      background: K.bg2, border: `1px solid ${K.bdr}`, borderLeft: `3px solid ${t.col}`,
                      borderRadius: 6, padding: '6px 10px', cursor: 'pointer',
                      fontFamily: "'Exo 2', sans-serif", fontSize: 11, fontWeight: 700,
                      color: K.t1, letterSpacing: 0.5, textTransform: 'uppercase',
                    }}>+ {t.label}</button>
                  ))}
                </div>
              </>
            )}
            <button onClick={resetDefaults} style={{
              marginTop: 10, background: 'transparent', border: `1px solid ${K.bdr}`,
              borderRadius: 6, padding: '5px 12px', cursor: 'pointer',
              fontSize: 10, color: K.t3, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase',
            }}>Reset to Defaults</button>
          </div>
        )}
      </div>

      {/* Nav Cards */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ ...LBL, marginBottom: 8 }}>Pages</div>
        <div className="hc-grid-2" style={{ gap: 10 }}>
          {MODE_CARDS.map((m) => (
            <button key={m.id} className="hc-btn" onClick={() => onSelectMode(m.id)}
              style={{
                background: K.bg1,
                border: `1.5px solid ${K.bdr}`,
                borderTop: `3px solid ${m.col}`,
                borderRadius: 10,
                padding: '14px 12px',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.15s',
              }}>
              <div style={{ marginBottom: 6 }}>
                <span style={{ ...BIG, fontSize: 16, color: m.col }}>{m.label}</span>
              </div>
              <div style={{ fontSize: 11, color: K.t2, lineHeight: 1.5, fontWeight: 500 }}>{m.sub}</div>
              <div style={{ marginTop: 10, fontFamily: K.fSans, fontSize: 11, color: m.col, fontWeight: 600, letterSpacing: 0.2 }}>Open →</div>
            </button>
          ))}
        </div>
      </div>

    </div>
  );
}
